import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { courtCasesApi, clientsApi, caseStatusesApi, extractOne, extractList } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const CourtCaseEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [advo_client_id, setAdvoClientId] = useState<number | ''>('');
  const [case_status, setCaseStatus] = useState<number>(1);
  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [is_active, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const { data: res, isLoading } = useQuery({
    queryKey: ['court-cases', numId],
    queryFn: () => courtCasesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  const { data: clientsRes } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list({}) });
  const clients = extractList(clientsRes);

  const { data: caseStatusesRes } = useQuery({ queryKey: ['case-statuses'], queryFn: () => caseStatusesApi.list() });
  const caseStatusesList = extractList(caseStatusesRes);

  useEffect(() => {
    if (!row || typeof row !== 'object') return;
    const r = row as any;
    if (r.advo_client_id != null) setAdvoClientId(r.advo_client_id);
    const statusId = r.case_status != null ? (typeof r.case_status === 'object' ? r.case_status?.id : r.case_status) : null;
    if (statusId != null) setCaseStatus(Number(statusId));
    if (r.priority != null) setPriority(r.priority === 'Low' ? 'Low' : r.priority === 'High' ? 'High' : 'Medium');
    if (r.is_active != null) setIsActive(r.is_active === 'No' ? 'No' : 'Yes');
  }, [row]);

  const update = useMutation({
    mutationFn: () =>
      courtCasesApi.update(numId, {
        advo_client_id: advo_client_id === '' ? undefined : (advo_client_id as number),
        case_status: typeof case_status === 'number' ? case_status : undefined,
        priority,
        is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-cases'] });
      toast.success('Court case updated.');
      navigate(`/dashboard/court-cases/${numId}`);
    },
    onError: (e: any) => toast.error(e?.message || 'Update failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate();
  };

  if (Number.isNaN(numId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border"><p className="text-slate-600">Invalid ID.</p><Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>Back</Button></div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">Edit Court Case</h1>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Only client, case status, priority and active can be updated.</p>
      {isLoading ? <p className="text-slate-500">Loading…</p> : (
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Client">
            <select className={inputClass} value={advo_client_id === '' ? '' : advo_client_id} onChange={(e) => setAdvoClientId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">Select client</option>
              {clients.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormField>
          <FormField label="Case status">
            <select className={inputClass} value={case_status} onChange={(e) => setCaseStatus(parseInt(e.target.value || '1', 10))}>
              <option value="">Select</option>
              {caseStatusesList.map((s: any) => (
                <option key={s.id} value={s.id}>{s.case_status_name ?? s.id}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Priority">
            <select className={inputClass} value={priority} onChange={(e) => setPriority(e.target.value as 'High' | 'Medium' | 'Low')}>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </FormField>
          <FormField label="Active">
            <select className={inputClass} value={is_active} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No')}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </FormField>
          <div className="md:col-span-2 flex gap-2 mt-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CourtCaseEditPage;
